import { Card, CardContent, Divider, Grid, Menu, MenuItem, Typography } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { FeedStyleClasses, GlobalClassKeys } from "assets/jss/common-styles";
import ButtonCardAction from "components/Buttons/ButtonCardAction";
import ButtonDashboard from "components/Buttons/ButtonDashboard";
import ButtonEllipsis from "components/Buttons/ButtonEllipsis";
import IconButtonEdit from "components/Buttons/IconButtonEdit";
import FormattedAvailability from "components/FormattedAvailability";
import IconCheckCircle from "components/Icons/IconCheckCircle";
import IconUserEdit from "components/Icons/IconUserEdit";
import { JoinComponents } from "components/JoinComponents";
import { GridEx } from "components/MuiExtras/Grid/GridEx";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import MuiWarning from "components/MuiExtras/Typography/MuiWarning";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import HtmlUtils from "lib/HtmlUtils";
import { KnowledgeTestStatus } from "models/ApiEnums";
import { KnowledgeTestAdminItemDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RoutePaths } from "routes/RouteConfig";
import { KnowledgeTestLevelName } from "views/Dashboards/components/KnowledgeCommons";
import KnowledgeTestDashboardDialog from "views/KnowledgeTest/Dialogs/KnowledgeTestDashboardDialog";
import KnowledgeTestLogDialog from "views/KnowledgeTest/Dialogs/KnowledgeTestLogDialog";
import WhoAnsweredKnowledgeTestDrawer from "views/KnowledgeTest/Dialogs/WhoAnsweredKnowledgeTestDrawer";
import { TopicsLine } from "views/Timeline/Components/CardComp/TopicsLine";

/////////////////////////////////////////////////////////////////////////////////////

interface IState {
    mouseOver: boolean;
    menuAnchorEl: any;
    dialog: any;
}

export interface IProps {
    item: KnowledgeTestAdminItemDto;
    onEdit: (testId: string) => void;
    onDelete: (testId: string) => void;
    onChangeActive?: (testId: string, blocked: boolean) => void;
    onNeedReload: () => void;
}

type Props = IProps & WithTranslation & WithSnackbar & WithStyles<typeof FeedStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////

class KnowledgeTestAdminCard extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);

        this.state = {
            dialog: null,
            mouseOver: false,
            menuAnchorEl: null
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.KnowledgeTest.Components.KnowledgeTestAdminCard." + r, f);
    }

    ///
    private handleCloseMenu() {
        this.setState({ menuAnchorEl: null, mouseOver: false });
    }

    ///
    private handleAudit = () => {
        this.setState({
            menuAnchorEl: null,
            mouseOver: false,
            dialog: <KnowledgeTestLogDialog
                testId={this.props.item.testId}
                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private handleWhoAnswered = () => {
        const { item: { testId } } = this.props;
        this.setState({
            dialog: (
                <WhoAnsweredKnowledgeTestDrawer
                    testId={testId}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handleDashboard = () => {
        const { item: { testId, title } } = this.props;
        this.setState({
            dialog: (
                <KnowledgeTestDashboardDialog
                    testId={testId}
                    title={title}
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handleEdit = () => {
        const { item: { testId } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onEdit(testId);
    };

    ///
    private handleDelete = () => {
        const { item: { testId } } = this.props;

        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onDelete(testId);
    };

    ///
    private handleBlock(testId: string, blocked: boolean) {
        this.setState({
            mouseOver: false,
            menuAnchorEl: null,
        });

        this.props.onChangeActive(testId, blocked);
    }

    ///
    private buildOverflowMenu() {

        const { item: { testId, status, permissions: { canDelete, canEdit } } } = this.props;

        const actionMenu: Array<any> = [];

        if (canEdit) {
            actionMenu.push(<MenuItem key="edit" onClick={this.handleEdit}>{this.props.t("Common.MenuAction.Edit")}</MenuItem>);

            if (this.props.onChangeActive) {
                if (status == KnowledgeTestStatus.Blocked) {
                    actionMenu.push(<MenuItem key="unblock" onClick={() => this.handleBlock(testId, false)}>{this.props.t("Common.MenuAction.Unblock")}</MenuItem>);
                }
                else if (status == KnowledgeTestStatus.AvailableToUse) {
                    actionMenu.push(<MenuItem key="block" onClick={() => this.handleBlock(testId, true)}><MuiWarning>{this.props.t("Common.MenuAction.Block")}</MuiWarning></MenuItem>);
                }
            }

            actionMenu.push(<Divider key="divAudit" />);
            actionMenu.push(<MenuItem key="audit" onClick={this.handleAudit}>{this.props.t("Common.MenuAction.Audit")}</MenuItem>);
        }

        if (canDelete) {
            if (actionMenu.length > 0) actionMenu.push(<Divider key="dividerDelete" />);
            actionMenu.push(<MenuItem key="delete" onClick={() => this.handleDelete()}><MuiColor variant="error">{this.props.t("Common.MenuAction.Delete")}</MuiColor></MenuItem>);
        }

        return (
            <>
                <ButtonEllipsis
                    onClick={
                        (e) => { this.setState({ menuAnchorEl: e.currentTarget }); }
                    }
                />
                <Menu
                    open={this.state.menuAnchorEl != null}
                    anchorEl={this.state.menuAnchorEl}
                    onClose={() => { this.handleCloseMenu(); }}
                >
                    {actionMenu}
                </Menu>
            </>
        );
    }

    ///
    public render() {
        const {
            classes,
            item: { topics, title, status, creator, difficultyLevel, applyTestEndDate, applyTestStartDate, expired, stats: { answers, correctAnswers }, permissions: { canEdit } }
        } = this.props;

        const { mouseOver } = this.state;

        const titleLine = (
            <span className={GlobalClassKeys.textLineBreak}>
                {title}
                {status == KnowledgeTestStatus.Blocked ? " " + this.res("BlockText") : ""}
                {status == KnowledgeTestStatus.WaitingMediaEncode ? " " + this.res("WaitingMediaEncodeText") : ""}
            </span>
        );

        return (
            <>
                {this.state.dialog}

                <Card className={classes.card}
                    onMouseEnter={() => this.setState({ mouseOver: true })}
                    onMouseLeave={() => this.setState({ mouseOver: false })
                    }
                >
                    <CardContent className={classes.cardContent}>

                        <MuiContainer margin="dense">
                            <Grid container direction="row" justifyContent="space-between" >
                                <GridEx item alignSelf="center">
                                    <TopicsLine
                                        topics={topics}
                                        onTopicClick={(id) => HtmlUtils.openUrlInNewWindow(RoutePaths.TopicViewer.replace(":id", id))}
                                    />
                                </GridEx>
                                <Grid item className={classes.cardActions}>
                                    <IconButtonEdit visible={mouseOver && canEdit} onClick={this.handleEdit} size="small" />
                                </Grid>
                            </Grid>
                        </MuiContainer>

                        <Grid container justifyContent="space-between" spacing={2} >
                            <GridEx item >
                                <Grid item className={GlobalClassKeys.pushVCenter}>
                                    <MuiClickable
                                        onClick={
                                            () => {
                                                HtmlUtils.openUrlInNewWindow(RoutePaths.UserProfileViewer.replace(":id?", creator.id));
                                            }
                                        }
                                        title="Ver perfil"
                                    >
                                        <img src={creator.thumbnailUrl} alt={creator.name} className={classes.publisherPhoto} />
                                    </MuiClickable>
                                </Grid>

                            </GridEx>
                            <Grid item xs >
                                <Typography variant="body2" component="p">
                                    {
                                        status == KnowledgeTestStatus.Blocked
                                            ? <MuiWarning>{titleLine}</MuiWarning>
                                            : status == KnowledgeTestStatus.WaitingMediaEncode
                                                ? <MuiWarning>{titleLine}</MuiWarning>
                                                : <b>{titleLine}</b>
                                    }
                                </Typography>
                                <small>
                                    <JoinComponents
                                        separator={<span>&nbsp;&bull;&nbsp;</span>}
                                        components={
                                            [
                                                <MuiMuted><KnowledgeTestLevelName value={difficultyLevel} /></MuiMuted>,
                                                (applyTestStartDate || applyTestEndDate)
                                                    ? <MuiColor variant={expired ? "warning" : "muted"}>
                                                        <FormattedAvailability startDate={applyTestStartDate} endDate={applyTestEndDate} />
                                                    </MuiColor>
                                                    : null
                                            ]
                                        }
                                    />
                                </small>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2} justifyContent={"space-between"} alignItems="baseline" className={classes.cardActions}>
                            <Grid item>
                                <Grid container alignItems="center">
                                    <Grid item>
                                        <ButtonCardAction
                                            title={this.res("TotalAnswersTitle")}
                                            startIcon={<IconUserEdit />}
                                            onClick={this.handleWhoAnswered}
                                        >
                                            <span>{answers}</span>
                                        </ButtonCardAction>
                                    </Grid>
                                    <Grid item>
                                        <ButtonCardAction
                                            title={this.res("TotalHitsTitle")}
                                            startIcon={<IconCheckCircle />}
                                            onClick={this.handleWhoAnswered}
                                        >
                                            <span>{(answers > 0 ? (100.0 * correctAnswers / answers) : 0).toFixed(0)}%</span>
                                        </ButtonCardAction>
                                    </Grid>
                                </Grid>
                            </Grid>

                            <Grid item >
                                <Grid container spacing={1} alignItems="baseline">
                                    <Grid item>
                                        <ButtonDashboard onClick={this.handleDashboard} />
                                    </Grid>
                                    <Grid item>
                                        {this.buildOverflowMenu()}
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </CardContent>

                </Card>
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FeedStyleClasses)(KnowledgeTestAdminCard)));